import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import {
  ArrowUp,
  ExternalLink,
  Loader2,
  Send,
  ShieldAlert,
  Sparkles,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toast } from '@/store/toastStore';
import { cn } from '@/lib/utils';
import { MarkdownContent } from '@/components/ai-sidebar/MarkdownContent';
import {
  chatWithAssistant,
  confirmSend,
  type AssistantResults,
  type ChatHistoryMessage,
  type SendProposal,
} from '@/services/aiAssistant.service';
import { useAuth } from '@/hooks/useAuth';
import { useIsTouchBand } from '@/hooks/useBreakpoint';
import { useVisualViewportVars } from '@/hooks/useVisualViewportVars';

interface UiTurn {
  role: 'user' | 'assistant';
  content: string;
  results?: AssistantResults | null;
  proposal?: SendProposal | null;
  errored?: boolean;
}

const SUGGESTIONS = [
  'Show me all members who haven\'t visited in the last 6 months',
  'Give me everyone who spent over $10,000 in the last year',
  'List patients whose last appointment was more than 90 days ago',
  'Patients added in the last 30 days with no completed visits',
];

export default function AIAssistantPage() {
  // Gate on the "AI Assistant" permission (id 26). The route only exists in
  // non-prod, so this also blocks direct-URL access there for unpermitted users.
  const { hasPermission } = useAuth();
  if (!hasPermission(26)) return <Navigate to="/" replace />;
  return <AIAssistantPageInner />;
}

function AIAssistantPageInner() {
  const [turns, setTurns] = useState<UiTurn[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setLoading] = useState(false);
  const [allowActions, setAllowActions] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Publishes `--vv-height` for the root's phone-tier height above. Enabled on the
  // whole phone tier rather than on a "thread is open" condition the way the
  // conversation pages do it, because this page is a composer at all times — there is
  // no state in which the keyboard can open and the height not matter.
  // The touch band, not `useIsBelow('md')`. A keyboard is raised by a finger, and
  // between 768px and 1023.98px — iPad portrait, every iPhone in landscape — this
  // hook was simply off: nothing published `--vv-height`, so the page kept its
  // pre-keyboard height and the composer sat under the browser bar. That is
  // SCRUM-1366's report, and it is the same gate mismatch SCRUM-1364 recorded.
  // `AISidebar` already gates its own copy on its visibility breakpoint; this page
  // was the surface left behind.
  useVisualViewportVars(useIsTouchBand());

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, isLoading]);

  async function submit(question: string) {
    const trimmed = question.trim();
    if (!trimmed || isLoading) return;

    const userTurn: UiTurn = { role: 'user', content: trimmed };
    const history: ChatHistoryMessage[] = turns
      .filter((t) => !t.errored)
      .map((t) => ({ role: t.role, content: t.content }));

    setTurns((prev) => [...prev, userTurn]);
    setInput('');
    setLoading(true);

    try {
      const res = await chatWithAssistant({ question: trimmed, allowActions, history });
      setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.answer || '(empty response)',
          results: res.results,
          proposal: res.proposal,
        },
      ]);
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Something went wrong fetching the response.';
      setTurns((prev) => [...prev, { role: 'assistant', content: message, errored: true }]);
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    submit(input);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit(input);
    }
  }

  return (
    // The shell releases the viewport lock below md (Layout.tsx: `max-md:h-auto
    // max-md:overflow-visible`) so pages scroll as a normal document. A chat surface
    // has to opt back out of that: with no definite height the composer drifts to the
    // end of the document instead of sitting above the browser bar, and the
    // scroll-to-latest effect below targets an inner scroller that would no longer be
    // the thing scrolling. 3rem is the shell's own `h-12 md:hidden` mobile header —
    // change that and change this.
    //
    // The height comes from the visual viewport, not from `dvh`. `dvh` tracks
    // collapsing browser chrome and NOT the on-screen keyboard, which never shrinks the
    // layout viewport — so a `100dvh` box keeps its full height while the visible band
    // shrinks underneath it, and this page's composer ends up below the fold with no
    // scrollable ancestor able to reveal it. That is the defect chunk P6 recorded and
    // SCRUM-1363 fixed for the AI overlay; both conversation pages already read
    // `--vv-height` the same way. The `var()` fallback is what applies under jsdom,
    // under the desktop-layout kill switch, and on any browser without the API.
    // `max-h-visual-viewport` is what carries the fix above 768px, where the shell
    // still owns the height and only the keyboard is unaccounted for. It composes with
    // the `max-md:` height rather than replacing it — see the rule in index.css.
    //
    // `touch-keyboard-surface` is the third report's fix and it addresses a different
    // thing from both of the above. Those two size the box; this one stops it being
    // MOVED. Sizing was never the defect here — `--vv-height` is the visible band, so
    // the height above already put the composer above the keyboard. The browser's own
    // scroll-to-reveal at focus time is what displaced it, and an in-flow root cannot
    // follow that back: the document is exactly one band tall inside a taller layout
    // viewport. Fixed, the document collapses to the mobile header and there is no
    // scroll left to displace. `--kb-surface-top` is that header's 3rem, and it is
    // scoped below the tablet boundary because the header renders on phones only.
    <div className="flex h-full flex-col bg-slate-50 max-md:h-[calc(var(--vv-height,100dvh)_-_3rem)] max-h-visual-viewport max-md:[--kb-surface-top:3rem] touch-keyboard-surface">
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <div className="border-b border-slate-200 bg-white max-md:shrink-0 touch-shrink-0">
        {/* Stacks below md — title row on top, the allow-actions card full width
            beneath it — because side by side the two clusters overflow a 375px
            viewport. PageHeader does the same thing, but this page hand-rolls its
            header and so never inherited that fix.

            Written as `max-md:` overrides appended to the existing classes rather
            than rewritten mobile-first with `md:` restoration. Both produce the same
            desktop CSS, but this way the desktop values are not retyped at all, so
            they cannot drift — see the SCRUM-1349 row in the migration log. */}
        <div className="flex items-center justify-between px-6 py-4 max-md:flex-col max-md:items-stretch max-md:gap-2 max-md:px-4 max-md:py-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-indigo-600 to-purple-600 shadow-sm max-md:shrink-0">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-slate-900">AI Assistant</h1>
              <p className="text-xs text-slate-500">
                Search the CRM in plain English. Staging only.
              </p>
            </div>
          </div>

          {/* Allow-actions toggle. The switch itself is 20x36 — under the 44px touch
              floor — but the whole card is the hit target: `button` is a labelable
              element, so a tap anywhere on this label activates it, and the card
              clears 44px. Resizing the switch would change its desktop look. */}
          <label className="flex cursor-pointer items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2 hover:border-slate-300 max-md:w-full">
            <ShieldAlert
              className={cn(
                'h-4 w-4 max-md:shrink-0',
                allowActions ? 'text-amber-500' : 'text-slate-400',
              )}
            />
            <div className="flex flex-col">
              <span className="text-xs font-medium text-slate-700 max-md:whitespace-nowrap">
                Allow actions
              </span>
              <span className="text-[10px] text-slate-400 max-md:whitespace-nowrap">
                SMS / email sends
              </span>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={allowActions}
              onClick={() => setAllowActions((v) => !v)}
              className={cn(
                // shrink-0 keeps the full w-9 track so the ON knob position stays
                // readable; ml-auto parks it at the right end of the full-width
                // card below md. Both are inert at md+, where the card is
                // auto-width and has no free space to distribute.
                'relative inline-flex h-5 w-9 items-center rounded-full transition-colors max-md:ml-auto max-md:shrink-0',
                allowActions ? 'bg-indigo-600' : 'bg-slate-300',
              )}
            >
              <span
                className={cn(
                  'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform',
                  allowActions ? 'translate-x-4' : 'translate-x-0.5',
                )}
              />
            </button>
          </label>
        </div>
      </div>

      {/* ─── Conversation ─────────────────────────────────────────────────── */}
      <div ref={scrollerRef} className="flex-1 overflow-y-auto px-4 py-6">
        <div className="mx-auto w-full max-w-4xl space-y-6">
          {turns.length === 0 && !isLoading && <EmptyState onPick={submit} />}

          {turns.map((turn, i) => (
            <TurnView key={i} turn={turn} allowActions={allowActions} />
          ))}

          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Thinking…
            </div>
          )}
        </div>
      </div>

      {/* ─── Composer ─────────────────────────────────────────────────────── */}
      {/* pb-safe-plus-3 clears the iOS home indicator / Android gesture bar. It is
          `calc(0.75rem + env(safe-area-inset-bottom, 0px))`, so where no inset is
          reported it resolves to exactly the 0.75rem the old `py-3` supplied — which
          is why it is safe unprefixed. */}
      <form
        onSubmit={onSubmit}
        className="border-t border-slate-200 bg-white px-4 pt-3 pb-safe-plus-3 max-md:shrink-0 touch-shrink-0"
      >
        <div className="mx-auto flex w-full max-w-4xl items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask anything about your patients…"
            rows={1}
            className="min-h-[44px] flex-1 resize-none rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-900 placeholder-slate-400 focus:border-indigo-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-indigo-100"
            disabled={isLoading}
          />
          <Button
            type="submit"
            aria-label="Send"
            disabled={isLoading || !input.trim()}
            className="h-11 bg-gradient-to-r from-indigo-600 to-purple-600 px-4 text-white shadow-sm hover:from-indigo-500 hover:to-purple-500 max-md:shrink-0"
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
        </div>
      </form>
    </div>
  );
}

// ─── Empty state ─────────────────────────────────────────────────────────────

function EmptyState({ onPick }: { onPick: (q: string) => void }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
      <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 shadow-sm">
        <Sparkles className="h-6 w-6 text-white" />
      </div>
      <h2 className="text-lg font-semibold text-slate-900">Search the CRM in plain English</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
        Ask about visits, spend, opt-ins, or any combination. Toggle{' '}
        <span className="font-medium text-slate-700">Allow actions</span> if you also want
        to draft outbound messages — every send is reviewed before it goes out.
      </p>
      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        {SUGGESTIONS.map((s) => (
          <button
            key={s}
            onClick={() => onPick(s)}
            className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 text-left text-sm text-slate-700 hover:border-indigo-300 hover:bg-indigo-50/40"
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

// ─── Single turn ─────────────────────────────────────────────────────────────

function TurnView({ turn, allowActions }: { turn: UiTurn; allowActions: boolean }) {
  if (turn.role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[80%] rounded-2xl bg-indigo-600 px-4 py-2.5 text-sm text-white shadow-sm">
          {turn.content}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-600 to-purple-600">
          <Sparkles className="h-3.5 w-3.5 text-white" />
        </div>
        <div
          className={cn(
            'rounded-2xl border bg-white px-4 py-3 text-sm shadow-sm',
            turn.errored ? 'border-rose-200 text-rose-700' : 'border-slate-200 text-slate-800',
          )}
        >
          <MarkdownContent content={turn.content} />
        </div>
      </div>

      {turn.results && <ResultsView results={turn.results} />}
      {turn.proposal && <ProposalView proposal={turn.proposal} allowActions={allowActions} />}
    </div>
  );
}

// ─── Result table ────────────────────────────────────────────────────────────

function ResultsView({ results }: { results: AssistantResults }) {
  const { total, patients, filter } = results;
  const filterChips = buildFilterChips(filter as Record<string, unknown>);

  if (patients.length === 0) {
    return (
      <div className="ml-10 rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-500">
        No patients match these filters.
      </div>
    );
  }

  return (
    <div className="ml-10 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50/60 px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-1.5">
          {filterChips.map((c) => (
            <span
              key={c}
              className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-medium text-indigo-700"
            >
              {c}
            </span>
          ))}
        </div>
        <span className="text-xs text-slate-500">
          {patients.length} of {total.toLocaleString()}
        </span>
      </div>

      <div className="max-h-[420px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-white">
            <tr className="border-b border-slate-100 text-left text-[11px] font-medium uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2">Patient</th>
              <th className="px-4 py-2">Last visit</th>
              <th className="px-4 py-2">Visits</th>
              <th className="px-4 py-2">Spend</th>
              <th className="px-4 py-2">Contact</th>
            </tr>
          </thead>
          <tbody>
            {patients.map((p) => (
              <tr key={p.patientID} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60">
                <td className="px-4 py-2">
                  <Link
                    to={`/patients/${p.patientID}`}
                    className="inline-flex items-center gap-1 font-medium text-blue-600 hover:underline"
                  >
                    {p.firstName} {p.lastName}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                </td>
                <td className="px-4 py-2 text-slate-600">{formatDate(p.lastVisit)}</td>
                <td className="px-4 py-2 text-slate-600">{p.apptCount}</td>
                <td className="px-4 py-2 text-slate-600">${p.totalSpend.toLocaleString()}</td>
                <td className="px-4 py-2 text-xs text-slate-500">
                  {p.emailOptIn && p.email ? <span className="mr-2">📧</span> : null}
                  {p.smsOptIn && p.cellPhone ? <span>💬</span> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Send proposal (confirmation panel) ──────────────────────────────────────

function ProposalView({
  proposal,
  allowActions,
}: {
  proposal: SendProposal;
  allowActions: boolean;
}) {
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState<null | { sent: number; skipped: number }>(null);
  const [cancelled, setCancelled] = useState(false);

  if (cancelled) {
    return (
      <div className="ml-10 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
        Send cancelled.
      </div>
    );
  }

  if (done) {
    return (
      <div className="ml-10 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
        Sent to <span className="font-semibold">{done.sent}</span> patient
        {done.sent === 1 ? '' : 's'}.
        {done.skipped > 0 ? (
          <span className="ml-1 text-emerald-700/80">
            ({done.skipped} skipped)
          </span>
        ) : null}
      </div>
    );
  }

  async function onConfirm() {
    if (!allowActions) {
      toast.error('Actions disabled', 'Enable the "Allow actions" toggle in the top right first.');
      return;
    }
    setSending(true);
    try {
      const res = await confirmSend({
        channel: proposal.channel,
        subject: proposal.subject,
        title: proposal.title,
        message: proposal.message,
        patient_ids: proposal.recipients.map((r) => r.patientID),
      });
      setDone({ sent: res.sent, skipped: res.skipped.length });
      toast.success(`Sent to ${res.sent}`, res.skipped.length ? `${res.skipped.length} skipped` : undefined);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Send failed.';
      toast.error('Send failed', message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="ml-10 overflow-hidden rounded-xl border border-amber-200 bg-amber-50/30 shadow-sm">
      <div className="flex items-center justify-between border-b border-amber-200 bg-amber-50 px-4 py-2.5">
        <div className="flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-600" />
          <span className="text-sm font-semibold text-amber-900">
            Confirm {proposal.channel.toUpperCase()} send
          </span>
          <span className="text-xs text-amber-700">
            to {proposal.recipients.length} recipient{proposal.recipients.length === 1 ? '' : 's'}
            {proposal.ineligible.length > 0
              ? ` (${proposal.ineligible.length} skipped — opt-out or missing contact)`
              : ''}
          </span>
        </div>
      </div>

      <div className="px-4 py-3">
        {proposal.channel === 'email' && proposal.subject && (
          <div className="mb-2 text-xs text-slate-600">
            <span className="font-medium text-slate-700">Subject:</span> {proposal.subject}
          </div>
        )}
        {proposal.channel === 'push' && proposal.title && (
          <div className="mb-2 text-xs text-slate-600">
            <span className="font-medium text-slate-700">Title:</span> {proposal.title}
          </div>
        )}
        <div className="mb-3 whitespace-pre-wrap rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-800">
          {proposal.message}
        </div>
        <p className="mb-2 text-[11px] uppercase tracking-wide text-slate-500">Recipients</p>
        <div className="max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white">
          <table className="w-full text-xs">
            <tbody>
              {proposal.recipients.map((r) => (
                <tr key={r.patientID} className="border-b border-slate-50 last:border-0">
                  <td className="px-3 py-1.5">
                    <Link
                      to={`/patients/${r.patientID}`}
                      className="font-medium text-blue-600 hover:underline"
                    >
                      {r.firstName} {r.lastName}
                    </Link>
                  </td>
                  <td className="px-3 py-1.5 text-slate-500">
                    {proposal.channel === 'sms'
                      ? r.cellPhone
                      : proposal.channel === 'email'
                        ? r.email
                        : 'app device'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-3 flex justify-end gap-2">
          <Button
            variant="ghost"
            disabled={sending}
            onClick={() => setCancelled(true)}
            className="text-slate-600"
          >
            <X className="mr-1 h-4 w-4" /> Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={sending || proposal.recipients.length === 0}
            className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-500 hover:to-purple-500"
          >
            {sending ? (
              <>
                <Loader2 className="mr-1 h-4 w-4 animate-spin" /> Sending…
              </>
            ) : (
              <>
                <Send className="mr-1 h-4 w-4" /> Confirm Send
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(d: string | null): string {
  if (!d) return '—';
  const date = new Date(d.replace(' ', 'T'));
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString();
}

function buildFilterChips(f: Record<string, unknown>): string[] {
  const chips: string[] = [];
  const fmtDate = (s: unknown) =>
    typeof s === 'string' ? s.slice(0, 10) : String(s);

  if (f.last_visit_before) chips.push(`Last visit ≤ ${fmtDate(f.last_visit_before)}`);
  if (f.last_visit_after) chips.push(`Last visit ≥ ${fmtDate(f.last_visit_after)}`);
  if (f.no_visits) chips.push('No visits');
  if (f.include_no_visits) chips.push('Incl. no visits');
  if (f.created_after) chips.push(`Added ≥ ${fmtDate(f.created_after)}`);
  if (f.created_before) chips.push(`Added ≤ ${fmtDate(f.created_before)}`);
  if (f.total_spend_gte != null) chips.push(`Spend ≥ $${f.total_spend_gte}`);
  if (f.total_spend_lte != null) chips.push(`Spend ≤ $${f.total_spend_lte}`);
  if (f.appointment_count_gte != null) chips.push(`Visits ≥ ${f.appointment_count_gte}`);
  if (f.email_opt_in === true) chips.push('Email opt-in');
  if (f.sms_opt_in === true) chips.push('SMS opt-in');
  if (f.has_email === true) chips.push('Has email');
  if (f.has_cell_phone === true) chips.push('Has cell');
  if (f.has_active_membership === true) chips.push('Active member');
  if (f.has_active_membership === false) chips.push('No active membership');
  if (Array.isArray(f.membership_template_names) && f.membership_template_names.length > 0) {
    chips.push(`Plan: ${(f.membership_template_names as string[]).join(', ')}`);
  }
  if (Array.isArray(f.tag_names) && f.tag_names.length > 0) {
    chips.push(`Tag: ${(f.tag_names as string[]).join(', ')}`);
  }
  if (f.name_contains) chips.push(`Name ~ "${f.name_contains}"`);
  return chips;
}
